import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Download, Loader2, Mail, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { downloadPdf } from "@/pages/Documenti";
import SendEmailDialog from "@/components/SendEmailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = {
  quote: ["bozza", "inviato", "accettato", "rifiutato"],
  proforma: ["bozza", "inviato", "accettato"],
  invoice: ["da incassare", "pagata", "insoluta"],
  credit_note: ["bozza", "inviata"],
  receipt: ["pagata", "da incassare"],
};

const TYPE_LABEL_KEYS = {
  quote: "docs.quote",
  invoice: "docs.invoice",
  credit_note: "docs.creditNote",
  proforma: "docs.proforma",
  receipt: "docs.receipt",
};

const DocumentoEditor = () => {
  const { id } = useParams();
  const { t, ts, lang } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [brief, setBrief] = useState("");
  const [mailOpen, setMailOpen] = useState(false);
  const [doc, setDoc] = useState({
    type: params.get("type") || "quote",
    client_id: "",
    client_name: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    tax_rate: 22,
    currency: "EUR",
    notes: "",
    status: params.get("type") === "invoice" ? "da incassare" : "bozza",
    items: [{ description: "", quantity: 1, unit_price: 0, unit: "", discount: 0 }],
    cassa_rate: 0,
    withholding_rate: 0,
    withholding_on_cassa: false,
    stamp_duty: 0,
    ref_number: "",
  });
  const fmt = (n) => money(n, doc.currency);
  const clientEmail =
    clients.find((c) => c.id === doc.client_id)?.email || doc.client_email || "";

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    if (!id && user)
      setDoc((d) => ({
        ...d,
        currency: user.currency || "EUR",
        tax_rate: user.default_tax_rate ?? d.tax_rate,
        cassa_rate: user.default_cassa_rate ?? 0,
        withholding_rate: user.default_withholding_rate ?? 0,
        withholding_on_cassa: user.withholding_on_cassa ?? false,
        stamp_duty: user.default_stamp_duty ?? 0,
      }));
    if (id)
      api.get(`/documents/${id}`).then(({ data }) =>
        setDoc({
          type: data.type,
          client_id: data.client?.id || "",
          client_name: data.client?.name || "",
          client_email: data.client?.email || "",
          issue_date: data.issue_date,
          due_date: data.due_date || "",
          tax_rate: data.tax_rate,
          currency: data.currency || "EUR",
          notes: data.notes || "",
          status: data.status,
          items: data.items.length
            ? data.items
            : [{ description: "", quantity: 1, unit_price: 0, unit: "", discount: 0 }],
          cassa_rate: data.cassa_rate || 0,
          withholding_rate: data.withholding_rate || 0,
          withholding_on_cassa: !!data.withholding_on_cassa,
          stamp_duty: data.stamp_duty || 0,
          ref_number: data.ref_number || "",
          number: data.number,
          id: data.id,
        })
      );
  }, [id, user]);

  const setItem = (i, key, value) =>
    setDoc((d) => {
      const items = [...d.items];
      const numeric = key !== "description" && key !== "unit";
      items[i] = { ...items[i], [key]: numeric ? Number(value) : value };
      return { ...d, items };
    });

  const subtotal = doc.items.reduce(
    (s, i) =>
      s +
      Number(i.quantity || 0) * Number(i.unit_price || 0) * (1 - Number(i.discount || 0) / 100),
    0
  );
  const cassa = (subtotal * Number(doc.cassa_rate || 0)) / 100;
  const taxable = subtotal + cassa;
  const tax = (taxable * Number(doc.tax_rate || 0)) / 100;
  const withholding =
    ((subtotal + (doc.withholding_on_cassa ? cassa : 0)) * Number(doc.withholding_rate || 0)) / 100;
  const total = taxable + tax + Number(doc.stamp_duty || 0) - withholding;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...doc, items: doc.items.filter((i) => i.description.trim()) };
      if (doc.id) {
        await api.put(`/documents/${doc.id}`, payload);
        toast.success(t("docs.updated"));
      } else {
        const { data } = await api.post("/documents", payload);
        toast.success(`${data.number} ✓`);
        navigate(`/documenti/${data.id}`, { replace: true });
        setDoc((d) => ({ ...d, id: data.id, number: data.number }));
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const draftWithAi = async () => {
    if (!brief.trim()) return toast.error(t("docs.aiNeedBrief"));
    setDrafting(true);
    try {
      const { data } = await api.post("/ai/draft-document", { brief, type: doc.type });
      if (!data.items?.length) {
        toast.error(t("docs.aiEmpty"));
        return;
      }
      setDoc((d) => ({
        ...d,
        client_name: d.client_id ? d.client_name : data.client_name || d.client_name,
        notes: data.notes || d.notes,
        tax_rate: data.tax_rate || d.tax_rate,
        items: data.items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity) || 1,
          unit_price: Number(i.unit_price) || 0,
          unit: i.unit || "",
          discount: 0,
        })),
      }));
      toast.success(t("docs.aiDone"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "AI error");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="document-editor">
      <div className="space-y-6 lg:col-span-2">
        <div className="card-flat relative overflow-hidden border-indigo-500/30 bg-slate-950 p-6 text-slate-100 grain">
          <div className="relative">
            <p className="flex items-center gap-2 text-sm font-extrabold tracking-tight">
              <Sparkles className="h-4 w-4 text-indigo-400" /> {t("docs.aiTitle")}
            </p>
            <p className="mt-1 text-xs text-slate-400">{t("docs.aiHint")}</p>
            <Textarea
              data-testid="ai-brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={3}
              className="mt-3 border-slate-800 bg-slate-900 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500"
              placeholder={t("docs.aiPlaceholder")}
            />
            <Button
              data-testid="ai-draft-button"
              onClick={draftWithAi}
              disabled={drafting}
              className="mt-3 gap-2 bg-indigo-600 text-white hover:bg-indigo-500"
            >
              {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {t("docs.aiFill")}
            </Button>          </div>
        </div>

        <div className="card-flat p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold md:text-lg">{t("docs.items")}</h2>
            <Button
              data-testid="add-item"
              variant="outline"
              onClick={() => setDoc((d) => ({ ...d, items: [...d.items, { description: "", quantity: 1, unit_price: 0, unit: "", discount: 0 }] }))}
              className="gap-2"
            >
              <Plus className="h-3.5 w-3.5" /> {t("common.add")}
            </Button>
          </div>
          <div className="mt-4 space-y-3">
            <div className="hidden gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-400 sm:grid sm:grid-cols-[1fr_70px_64px_100px_64px_36px]">
              <span>{t("docs.description")}</span>
              <span className="text-right">{t("docs.qty")}</span>
              <span className="text-center">{t("docs.unit")}</span>
              <span className="text-right">{t("docs.price")}</span>
              <span className="text-right">{t("docs.discountShort")}</span>
              <span />
            </div>
            {doc.items.map((it, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[1fr_70px_64px_100px_64px_36px]">
                <Input
                  data-testid={`item-desc-${i}`}
                  value={it.description}
                  onChange={(e) => setItem(i, "description", e.target.value)}
                  placeholder={t("docs.description")}
                  className="focus:ring-2 focus:ring-blue-600"
                />
                <Input
                  data-testid={`item-qty-${i}`}
                  type="number"
                  step="0.5"
                  value={it.quantity}
                  onChange={(e) => setItem(i, "quantity", e.target.value)}
                  className="font-mono-num text-right focus:ring-2 focus:ring-blue-600"
                />
                <Input
                  data-testid={`item-unit-${i}`}
                  value={it.unit || ""}
                  onChange={(e) => setItem(i, "unit", e.target.value)}
                  placeholder={t("docs.unit")}
                  className="text-center focus:ring-2 focus:ring-blue-600"
                />
                <Input
                  data-testid={`item-price-${i}`}
                  type="number"
                  step="0.01"
                  value={it.unit_price}
                  onChange={(e) => setItem(i, "unit_price", e.target.value)}
                  className="font-mono-num text-right focus:ring-2 focus:ring-blue-600"
                />
                <Input
                  data-testid={`item-discount-${i}`}
                  type="number"
                  step="1"
                  value={it.discount || 0}
                  onChange={(e) => setItem(i, "discount", e.target.value)}
                  title={t("docs.discount")}
                  className="font-mono-num text-right focus:ring-2 focus:ring-blue-600"
                />
                <button
                  data-testid={`remove-item-${i}`}
                  onClick={() => setDoc((d) => ({ ...d, items: d.items.filter((_, k) => k !== i) }))}
                  className="rounded-md text-slate-400 transition-colors duration-200 hover:text-red-600"
                >
                  <Trash2 className="mx-auto h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-1.5 border-t border-slate-200 pt-4 text-sm">
            <div className="flex justify-between text-slate-600">
              <span>{t("common.subtotal")}</span>
              <span className="font-mono-num" data-testid="doc-subtotal">{fmt(subtotal)}</span>
            </div>
            {cassa > 0 && (
              <>
                <div className="flex justify-between text-slate-600">
                  <span>{t("docs.cassa")} {doc.cassa_rate}%</span>
                  <span className="font-mono-num" data-testid="doc-cassa">{fmt(cassa)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>{t("docs.taxable")}</span>
                  <span className="font-mono-num">{fmt(taxable)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-slate-600">
              <span>{t("docs.vatRate").replace("%","")} {doc.tax_rate}%</span>
              <span className="font-mono-num">{fmt(tax)}</span>
            </div>
            {Number(doc.stamp_duty) > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>{t("docs.stamp")}</span>
                <span className="font-mono-num" data-testid="doc-stamp">{fmt(Number(doc.stamp_duty))}</span>
              </div>
            )}
            {withholding > 0 && (
              <div className="flex justify-between text-amber-700">
                <span>{t("docs.withholding")} {doc.withholding_rate}%</span>
                <span className="font-mono-num" data-testid="doc-withholding">- {fmt(withholding)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-semibold">
              <span>{withholding > 0 ? t("docs.netToPay") : t("common.total")}</span>
              <span className="font-mono-num" data-testid="doc-total">{fmt(total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="card-flat space-y-4 p-6">
          <div>
            <Label>{t("docs.type")}</Label>
            <select
              data-testid="doc-type"
              value={doc.type}
              onChange={(e) =>
                setDoc((d) => ({ ...d, type: e.target.value, status: STATUSES[e.target.value][0] }))
              }
              disabled={!!doc.id}
              className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
            >
              <option value="quote">{t("docs.quote")}</option>
              <option value="invoice">{t("docs.invoice")}</option>
              <option value="proforma">{t("docs.proforma")}</option>
              <option value="receipt">{t("docs.receipt")}</option>
              <option value="credit_note">{t("docs.creditNote")}</option>
            </select>
          </div>
          {doc.number && (
            <p className="font-mono-num text-sm font-semibold" data-testid="doc-number">
              {doc.number} · {t(TYPE_LABEL_KEYS[doc.type] || "docs.invoice")}
            </p>
          )}
          <div>
            <Label>{t("common.client")}</Label>
            <select
              data-testid="doc-client"
              value={doc.client_id}
              onChange={(e) => setDoc((d) => ({ ...d, client_id: e.target.value }))}
              className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
            >
              <option value="">{t("docs.freeClient")}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          {!doc.client_id && (
            <div>
              <Label>{t("docs.clientName")}</Label>
              <Input
                data-testid="doc-client-name"
                value={doc.client_name}
                onChange={(e) => setDoc((d) => ({ ...d, client_name: e.target.value }))}
                className="mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("common.date")}</Label>
              <Input
                data-testid="doc-issue-date"
                type="date"
                value={doc.issue_date}
                onChange={(e) => setDoc((d) => ({ ...d, issue_date: e.target.value }))}
                className="mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <Label>{t("common.due")}</Label>
              <Input
                data-testid="doc-due-date"
                type="date"
                value={doc.due_date}
                onChange={(e) => setDoc((d) => ({ ...d, due_date: e.target.value }))}
                className="mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("docs.vatRate")}</Label>
              <Input
                data-testid="doc-tax"
                type="number"
                value={doc.tax_rate}
                onChange={(e) => setDoc((d) => ({ ...d, tax_rate: Number(e.target.value) }))}
                className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <Label>{t("settings.currency")}</Label>
              <select
                data-testid="doc-currency"
                value={doc.currency}
                onChange={(e) => setDoc((d) => ({ ...d, currency: e.target.value }))}
                className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
              >
                {["EUR", "USD", "GBP", "CHF", "CAD", "AUD", "SEK", "NOK", "DKK", "PLN"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          {(lang === "it" || doc.currency === "EUR") && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="fiscal-card">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                {t("docs.fiscalTitle")}
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("docs.cassaRate")}</Label>
                  <Input
                    data-testid="doc-cassa-rate"
                    type="number"
                    step="0.5"
                    value={doc.cassa_rate}
                    onChange={(e) => setDoc((d) => ({ ...d, cassa_rate: Number(e.target.value) }))}
                    className="font-mono-num mt-1 h-9 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("docs.withholdingRate")}</Label>
                  <Input
                    data-testid="doc-withholding-rate"
                    type="number"
                    step="1"
                    value={doc.withholding_rate}
                    onChange={(e) =>
                      setDoc((d) => ({ ...d, withholding_rate: Number(e.target.value) }))
                    }
                    className="font-mono-num mt-1 h-9 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("docs.stamp")}</Label>
                  <Input
                    data-testid="doc-stamp-duty"
                    type="number"
                    step="0.01"
                    value={doc.stamp_duty}
                    onChange={(e) => setDoc((d) => ({ ...d, stamp_duty: Number(e.target.value) }))}
                    className="font-mono-num mt-1 h-9 bg-white"
                  />
                </div>
                {doc.type === "credit_note" && (
                  <div>
                    <Label className="text-xs">{t("docs.refNumber")}</Label>
                    <Input
                      data-testid="doc-ref-number"
                      value={doc.ref_number}
                      onChange={(e) => setDoc((d) => ({ ...d, ref_number: e.target.value }))}
                      className="font-mono-num mt-1 h-9 bg-white"
                    />
                  </div>
                )}
              </div>
              {Number(doc.withholding_rate) > 0 && Number(doc.cassa_rate) > 0 && (
                <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                  <input
                    data-testid="doc-withholding-on-cassa"
                    type="checkbox"
                    checked={doc.withholding_on_cassa}
                    onChange={(e) =>
                      setDoc((d) => ({ ...d, withholding_on_cassa: e.target.checked }))
                    }
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  {t("docs.withholdingOnCassa")}
                </label>
              )}
            </div>
          )}
          <div>
            <Label>{t("common.status")}</Label>
            <select
              data-testid="doc-status"
              value={doc.status}
              onChange={(e) => setDoc((d) => ({ ...d, status: e.target.value }))}
              className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-600"
            >
              {STATUSES[doc.type].map((s) => (
                <option key={s} value={s}>{ts(s)}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>{t("common.notes")}</Label>
            <Textarea
              data-testid="doc-notes"
              rows={3}
              value={doc.notes}
              onChange={(e) => setDoc((d) => ({ ...d, notes: e.target.value }))}
              className="mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <Button
            data-testid="save-document"
            onClick={save}
            disabled={saving}
            className="w-full bg-blue-600 text-white hover:bg-blue-700"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("docs.saveDoc")}
          </Button>
          {doc.id && (
            <Button
              data-testid="download-pdf"
              variant="outline"
              onClick={() => downloadPdf(doc)}
              className="w-full gap-2"
            >
              <Download className="h-4 w-4" /> {t("docs.downloadPdf")}
            </Button>
          )}
          {doc.id && (
            <Button
              data-testid="send-email-button"
              variant="outline"
              onClick={() => setMailOpen(true)}
              className="w-full gap-2"
            >
              <Mail className="h-4 w-4" /> {t("mail.send")}
            </Button>
          )}
        </div>
      </div>

      {mailOpen && (
        <SendEmailDialog
          open={mailOpen}
          doc={{ id: doc.id, number: doc.number, client: { email: clientEmail } }}
          onClose={() => setMailOpen(false)}
        />
      )}
    </div>
  );
};

export default DocumentoEditor;
